import React, { useState } from 'react';
import { Patient } from '../types/patient';
import { ArrowLeft, Calendar, User, FileText } from 'lucide-react';
import { getBadgeClasses, getRiskIcon } from '../utils/styleHelpers';
import Modal from './Modal';
// @ts-ignore
import jsPDF from 'jspdf';

interface PatientHeaderProps {
  patient: Patient;
  onBack: () => void;
}

export default function PatientHeader({ patient, onBack }: PatientHeaderProps) {
  const RiskIcon = getRiskIcon(patient.riskLevel as 'low' | 'medium' | 'high');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [pdfError, setPdfError] = useState<string>('');

  const generatePatientReport = async () => {
    setIsGenerating(true);

    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.text('Your Health, Your Plan', 20, 30);
      doc.setFontSize(16);
      doc.text(`Patient Report: ${patient.firstName} ${patient.lastName}`, 20, 45);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 55);
      doc.text(`MRN: ${patient.mrn}`, 20, 62);
      doc.text(`DOB: ${patient.dateOfBirth}`, 20, 69);

      let yPosition = 85;

      // Health Summary
      doc.setFontSize(14);
      doc.text('Health Summary', 20, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.text(`Risk Level: ${patient.riskLevel.toUpperCase()}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Blood Pressure: ${patient.healthMetrics.cardiovascularAnalysis.systolic}/${patient.healthMetrics.cardiovascularAnalysis.diastolic} mmHg`, 20, yPosition);
      yPosition += 8;
      doc.text(`BMI: ${patient.healthMetrics.obesityAnalysis.bmi}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Total Cholesterol: ${patient.healthMetrics.cholesterolAnalysis.total} mg/dL`, 20, yPosition);
      yPosition += 8;
      doc.text(`LDL: ${patient.healthMetrics.cholesterolAnalysis.ldl} mg/dL`, 20, yPosition);
      yPosition += 8;
      doc.text(`HDL: ${patient.healthMetrics.cholesterolAnalysis.hdl} mg/dL`, 20, yPosition);
      yPosition += 15;

      // Lab Results
      if (patient.labResults.length > 0) {
        doc.setFontSize(14);
        doc.text('Recent Lab Results', 20, yPosition);
        yPosition += 10;

        const recentLabs = patient.labResults.slice(0, 10);
        recentLabs.forEach((result) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.setFontSize(10);
          doc.text(`${result.name}: ${result.value} ${result.unit} (${result.status})`, 20, yPosition);
          yPosition += 8;
        });
        yPosition += 10;
      }

      // Medications
      if (patient.medications.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFontSize(14);
        doc.text('Current Medications', 20, yPosition);
        yPosition += 10;

        patient.medications.forEach((med) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.setFontSize(10);
          doc.text(`${med.name} - ${med.dosage} (${med.overallStatus})`, 20, yPosition);
          yPosition += 8;
        });
        yPosition += 10;
      }

      // Allergies
      if (patient.allergies.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFontSize(14);
        doc.text('Allergies', 20, yPosition);
        yPosition += 10;

        patient.allergies.forEach((allergy) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.setFontSize(10);
          doc.text(`${allergy.allergen} - ${allergy.severity} (${allergy.reaction})`, 20, yPosition);
          yPosition += 8;
        });
      }

      // Save the PDF
      doc.save(`${patient.firstName}_${patient.lastName}_Health_Report.pdf`);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateReport = () => {
    const patientFullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
    const isBarbaraSmith = patientFullName === 'barbara smith';
    const isJacobFenton = patientFullName === 'jacob fenton';

    if (isBarbaraSmith || isJacobFenton) {
      openPdfModal();
    } else {
      generatePatientReport();
    }
  };

  const openPdfModal = () => {
    const firstName = patient.firstName.toLowerCase();
    const lastName = patient.lastName.toLowerCase();
    const path = `/data_store/${firstName}_${lastName}_report.pdf`;

    setPdfUrl(path);
    setIsModalOpen(true);
    setIsGenerating(true);
    setPdfError('');
  };

  const closePdfModal = () => {
    setIsModalOpen(false);
    setPdfUrl('');
    setPdfError('');
    setIsGenerating(false);
  };

  const handlePdfLoad = () => {
    setIsGenerating(false);
  };

  const handlePdfError = () => {
    setIsGenerating(false);
    setPdfError('Unable to load the PDF report. The file may be missing or your browser cannot display it.');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-brand-taupe-300 p-4 hover:border-brand-taupe-400 transition-all">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-brand-taupe-500 hover:text-brand-dark-600 hover:bg-brand-beige-100 rounded-lg transition-colors"
            aria-label="Back to patient list"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="h-12 w-12 bg-brand-beige-300 rounded-full flex items-center justify-center">
            <span className="text-brand-dark-600 font-bold text-lg">
              {patient.firstName[0]}{patient.lastName[0]}
            </span>
          </div>

          <div>
            <h1 className="text-xl font-bold text-brand-dark-600">
              {patient.firstName} {patient.lastName}
            </h1>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-brand-dark-400">
              <div className="flex items-center space-x-1">
                <User className="w-4 h-4" aria-hidden="true" />
                <span>MRN: {patient.mrn}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" aria-hidden="true" />
                <span>DOB: {patient.dateOfBirth}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" aria-hidden="true" />
                <span>Last Visit: {patient.lastVisit}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className="flex items-center space-x-2 px-4 py-2 bg-brand-dark-500 text-white rounded-lg hover:bg-brand-dark-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Generate patient report"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="text-sm font-medium">Generating...</span>
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                <span className="text-sm font-medium">Generate Report</span>
              </>
            )}
          </button>

          <div className={getBadgeClasses(patient.riskLevel as 'low' | 'medium' | 'high', 'md')}>
            <RiskIcon className="w-4 h-4 mr-1" aria-hidden="true" />
            <span className="capitalize">{patient.riskLevel} Risk</span>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closePdfModal}
        title={`Patient Report - ${patient.firstName} ${patient.lastName}`}
        size="xl"
      >
        {isGenerating && !pdfError && (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-dark-500 mx-auto mb-4"></div>
              <p className="text-brand-dark-400">Loading report...</p>
            </div>
          </div>
        )}

        {pdfError && (
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{pdfError}</p>
            <button
              onClick={closePdfModal}
              className="px-4 py-2 bg-brand-dark-500 text-white rounded-lg hover:bg-brand-dark-600 transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {!pdfError && (
          <iframe
            src={pdfUrl}
            className="w-full h-[70vh] border-0"
            title={`${patient.firstName} ${patient.lastName} Health Report`}
            onLoad={handlePdfLoad}
            onError={handlePdfError}
          />
        )}
      </Modal>
    </div>
  );
}
